import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import PageHero from "@/components/feature/PageHero";
import { categories } from "@/mocks/categories";

const subjectOptions = ["投稿推荐漫画", "内容纠错", "题材建议", "合作洽谈", "其他"];

interface FormState {
  status: "idle" | "loading" | "success" | "error";
  message: string;
}

export default function ContactPage() {
  const [formState, setFormState] = useState<FormState>({ status: "idle", message: "" });

  useEffect(() => {
    document.title = "联系我们 · 妖精漫画堂 韩国妖精漫画奇幻异世界在线看";
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 · 妖精漫画堂",
    inLanguage: "zh-CN",
    isPartOf: { "@type": "WebSite", name: "妖精漫画堂" },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const formData = new FormData(form);
    const honey = (formData.get("website_alt") as string | null)?.trim() ?? "";
    if (honey) {
      setFormState({ status: "success", message: "提交成功，感谢你的反馈，我们会尽快处理。" });
      return;
    }

    const email = (formData.get("email") as string)?.trim() ?? "";
    const name = (formData.get("name") as string)?.trim() ?? "";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormState({ status: "error", message: "请输入有效的邮箱地址，方便我们回复你。" });
      return;
    }
    if (!name) {
      setFormState({ status: "error", message: "请填写你的称呼。" });
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setFormState({ status: "loading", message: "正在提交..." });
    try {
      const res = await fetch("https://readdy.ai/api/form/da2kcbiamq5jpe1hrut0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const okCode = parsed?.code === "OK";
      if (res.ok && okCode && !/spam/i.test(String(serverMsg))) {
        setFormState({ status: "success", message: "提交成功，感谢你的反馈，我们会尽快回复。" });
        form.reset();
      } else {
        setFormState({ status: "error", message: /spam/i.test(String(serverMsg)) ? "提交未通过校验，请检查内容后重试。" : (serverMsg || "提交失败，请稍后重试。") });
      }
    } catch {
      setFormState({ status: "error", message: "网络异常，提交失败，请稍后重试。" });
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="联系我们"
        description="投稿推荐你爱的妖精漫画、提出建议或反馈问题，都欢迎留言。我们珍视每一位读者的声音。"
        image="https://readdy.ai/api/search-image?query=magical%20envelope%20with%20glowing%20fairy%20dust%20and%20paper%20plane%20soaring%20over%20a%20fantasy%20landscape%2C%20stylized%20digital%20illustration%2C%20emerald%20and%20gold%20palette&width=1600&height=600&seq=contact-banner&orientation=landscape"
        crumbs={[
          { label: "首页", to: "/" },
          { label: "联系我们" },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="container-site grid gap-10 lg:grid-cols-[1fr_340px]">
          <div className="card p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-600">
              我们会在 1-2 个工作日内回复你的留言，请填写真实有效的联系方式。
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="联系我们与漫画投稿表单" className="mt-6 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼 <span className="text-secondary-500">*</span></label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱 <span className="text-secondary-500">*</span></label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={60}
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">联系主题</label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  {subjectOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容 <span className="text-secondary-500">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="想投稿的作品、希望改进的地方，或任何想说的话……"
                  className="w-full resize-y rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              {formState.status === "success" && (
                <div className="flex items-start gap-2.5 rounded-md bg-primary-50 p-3.5 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  {formState.message}
                </div>
              )}
              {formState.status === "error" && (
                <div className="flex items-start gap-2.5 rounded-md bg-secondary-50 p-3.5 text-sm text-secondary-800">
                  <i className="ri-error-warning-line mt-0.5" />
                  {formState.message}
                </div>
              )}

              <button type="submit" disabled={formState.status === "loading"} className="btn btn-primary w-full py-3 disabled:opacity-60 sm:w-auto">
                {formState.status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    提交留言
                  </>
                )}
              </button>
            </form>
          </div>

          <aside className="space-y-6">
            <div className="card p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900">联系方式</h3>
              <ul className="mt-4 space-y-3.5 text-sm text-foreground-600">
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className="ri-mail-line" /></span>
                  fairy@example.com
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className="ri-time-line" /></span>
                  工作日 9:00 - 18:00
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className="ri-message-3-line" /></span>
                  投稿与建议随时欢迎
                </li>
              </ul>
            </div>
            <div className="card p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900">想投稿的作品属于哪个题材？</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <span key={c.slug} className="chip">{c.name}</span>
                ))}
                <span className="chip">魔法奇幻</span>
                <span className="chip">其他</span>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  );
}